import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRightLeft, Clock, Pause, Play, Plus, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { QuickLogTimeDialog } from '../components/QuickLogTimeDialog'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody } from '../components/ui/Card'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Select } from '../components/ui/Input'
import { apiClient } from '../lib/api'
import { cn, formatHours, formatRelative } from '../lib/utils'

type Report = {
  from: string
  to: string
  groupBy: string
  mode: 'tracked' | 'estimated'
  totalSeconds: number
  totalHours: number
  rows: { key: string; label: string; seconds: number; hours: number; sessionCount: number }[]
}

type LiveSession = {
  id: string
  taskId: string
  taskTitle: string
  taskKeywords: string[]
  featureTitle: string | null
  projectName: string | null
  clientName: string | null
  startedAt: string
  endedAt: string | null
  lastTouchedAt: string
  durationSeconds: number
  isOpen: boolean
  claudeSessionId: string | null
  promptPreview: string | null
  matchLevel: string | null
  matchConfidence: string | null
  matchReason: string | null
  workingDir: string | null
}

type LiveResult = { results: LiveSession[]; total: number }
type Task = { id: string; title: string; keywords?: string[] }

const ranges = {
  today: { label: 'Oggi', days: 1 },
  week: { label: 'Ultimi 7 giorni', days: 7 },
  month: { label: 'Ultimi 30 giorni', days: 30 },
  quarter: { label: 'Ultimi 90 giorni', days: 90 },
}

/**
 * Compute the [from, to] interval for the selected range.
 *
 * - 'today': from local midnight (00:00 of the current day, user's timezone)
 *   to now. Calendar-based, NOT a rolling 24h window — picking "Oggi" at
 *   16:00 should NOT show entries from 16:00 yesterday.
 * - 'week' / 'month' / 'quarter': rolling N-days back from now, matching
 *   their labels ("Ultimi 7/30/90 giorni").
 *
 * `to` is the current instant in both cases; the backend filter is
 * `started_at >= from && started_at < to`.
 */
function rangeInterval(range: keyof typeof ranges, now: Date): { from: Date; to: Date } {
  if (range === 'today') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
    return { from: start, to: now }
  }
  return {
    from: new Date(now.getTime() - ranges[range].days * 24 * 60 * 60 * 1000),
    to: now,
  }
}

export function TimePage() {
  const [tab, setTab] = useState<'report' | 'live'>('report')
  const [quickLogOpen, setQuickLogOpen] = useState(false)

  return (
    <AppLayout>
      <div className="flex items-start justify-between gap-4 mb-2">
        <PageHeader title="Tempi" description="Report e sessioni live" />
        <Button onClick={() => setQuickLogOpen(true)} className="shrink-0 mt-1">
          <Plus className="size-4" />
          Quick Log
        </Button>
      </div>
      <div className="mb-4 flex gap-1 border-b border-zinc-800">
        <TabButton active={tab === 'report'} onClick={() => setTab('report')}>
          <Clock className="size-4" /> Report
        </TabButton>
        <TabButton active={tab === 'live'} onClick={() => setTab('live')}>
          <Sparkles className="size-4" /> Live
        </TabButton>
      </div>
      {tab === 'report' ? <ReportView /> : <LiveView />}
      <QuickLogTimeDialog open={quickLogOpen} onClose={() => setQuickLogOpen(false)} />
    </AppLayout>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2 text-sm font-medium transition border-b-2 -mb-px',
        active
          ? 'text-cyan-300 border-cyan-400'
          : 'text-zinc-400 border-transparent hover:text-zinc-200',
      )}
    >
      {children}
    </button>
  )
}

// ============================================================
// Report tab (existing)
// ============================================================

type Project = { id: string; name: string }

function ReportView() {
  const [range, setRange] = useState<keyof typeof ranges>('week')
  const [groupBy, setGroupBy] = useState<'task' | 'project' | 'client' | 'day'>('task')
  const [mode, setMode] = useState<'tracked' | 'estimated'>('tracked')
  const [projectId, setProjectId] = useState<string>('')

  const now = new Date()
  const { from, to } = rangeInterval(range, now)
  const params = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
    groupBy,
    mode,
  })
  if (projectId) params.set('projectId', projectId)

  const { data, isLoading } = useQuery({
    queryKey: ['time', 'report', range, groupBy, mode, projectId],
    queryFn: () => apiClient.get<Report>(`/time/report?${params}`),
  })

  const projectsQuery = useQuery({
    queryKey: ['projects', { all: true }],
    queryFn: () => apiClient.get<{ results: Project[] }>('/projects?limit=200'),
  })

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <div className="inline-flex rounded-md border border-zinc-800 bg-zinc-950 p-0.5">
          <ModeButton active={mode === 'tracked'} onClick={() => setMode('tracked')}>
            Tracked
          </ModeButton>
          <ModeButton active={mode === 'estimated'} onClick={() => setMode('estimated')}>
            Estimated
          </ModeButton>
        </div>
        <div className="flex gap-2 ml-auto">
          <Select
            value={projectId}
            onChange={(e) => setProjectId(e.currentTarget.value)}
            className="min-w-[180px]"
          >
            <option value="">Tutti i progetti</option>
            {projectsQuery.data?.results.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <Select value={range} onChange={(e) => setRange(e.target.value as never)}>
            {Object.entries(ranges).map(([key, r]) => (
              <option key={key} value={key}>
                {r.label}
              </option>
            ))}
          </Select>
          <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value as never)}>
            <option value="task">Per task</option>
            <option value="project">Per progetto</option>
            <option value="client">Per cliente</option>
            <option value="day">Per giorno</option>
          </Select>
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="mb-3 flex items-end justify-between gap-3">
            <p className="text-xs text-zinc-500">
              {mode === 'tracked'
                ? 'Ore tracciate dagli hook nel periodo (lavoro effettivo).'
                : 'Somma di estimatedHours dei task con completedAt nel periodo (lavoro consegnato secondo stima).'}
            </p>
            {data && (
              <div className="text-sm text-zinc-400 shrink-0">
                Totale:{' '}
                <span className="text-zinc-100 font-medium">{formatHours(data.totalHours)}</span>
              </div>
            )}
          </div>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : data && data.rows.length > 0 ? (
            <ul className="space-y-2">
              {data.rows.map((row) => {
                const max = Math.max(...data.rows.map((r) => r.seconds), 1)
                const pct = Math.round((row.seconds / max) * 100)
                return (
                  <li key={row.key} className="grid grid-cols-[1fr_auto] gap-3 items-center">
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-200 truncate">{row.label}</div>
                      <div className="mt-1.5 h-2 rounded-full bg-zinc-900/80 border border-zinc-800/60 overflow-hidden">
                        <div
                          className={cn(
                            'h-full transition-[width] duration-500',
                            mode === 'estimated'
                              ? 'bg-gradient-to-r from-emerald-500 to-cyan-400 shadow-[0_0_10px_0_rgba(52,211,153,0.45)]'
                              : 'bg-gradient-to-r from-cyan-500 via-cyan-400 to-violet-500 shadow-[0_0_10px_0_rgba(34,211,238,0.45)]',
                          )}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm tabular-nums text-zinc-100">
                        {formatHours(row.hours)}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {row.sessionCount} {mode === 'estimated' ? 'task' : 'sessioni'}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <Empty
              icon={Clock}
              title="Nessun dato"
              description={
                mode === 'tracked'
                  ? 'Nessuna sessione registrata nel periodo selezionato.'
                  : 'Nessun task chiuso nel periodo selezionato.'
              }
            />
          )}
        </CardBody>
      </Card>
    </>
  )
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 text-xs font-medium rounded transition',
        active
          ? 'bg-cyan-500/15 text-cyan-200 border border-cyan-500/40'
          : 'text-zinc-400 hover:text-zinc-200 border border-transparent',
      )}
    >
      {children}
    </button>
  )
}

// ============================================================
// Live tab — sessioni in tempo reale con riattribuzione
// ============================================================

function LiveView() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['time', 'live'],
    queryFn: () => apiClient.get<LiveResult>('/time/live?limit=50'),
    refetchInterval: 15000, // refresh ogni 15s
  })

  const [reassigning, setReassigning] = useState<LiveSession | null>(null)

  return (
    <>
      <Card>
        <CardBody className="!p-0">
          {isLoading ? (
            <div className="p-5 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : data && data.results.length > 0 ? (
            <ul className="divide-y divide-zinc-800">
              {data.results.map((s) => (
                <li
                  key={s.id}
                  className={cn(
                    'px-5 py-3',
                    s.isOpen && 'bg-emerald-500/5 border-l-2 border-emerald-500/40',
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {s.isOpen ? (
                          <Badge variant="success" className="gap-1 px-2">
                            <Play className="size-3" /> aperta
                          </Badge>
                        ) : (
                          <Badge variant="muted" className="gap-1 px-2">
                            <Pause className="size-3" /> chiusa
                          </Badge>
                        )}
                        {s.matchLevel && (
                          <Badge
                            variant={
                              s.matchLevel === 'L1'
                                ? 'success'
                                : s.matchLevel === 'L2'
                                  ? 'info'
                                  : s.matchLevel === 'MANUAL'
                                    ? 'warning'
                                    : 'muted'
                            }
                          >
                            {s.matchLevel} {s.matchConfidence ? `· ${s.matchConfidence}` : ''}
                          </Badge>
                        )}
                        <span className="text-xs text-zinc-500">
                          {formatHours(s.durationSeconds / 3600)}
                        </span>
                        <span className="text-xs text-zinc-500">·</span>
                        <span className="text-xs text-zinc-500">
                          attiva {formatRelative(s.lastTouchedAt)}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-zinc-100 mt-1 truncate">
                        {s.taskTitle}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">
                        {[s.clientName, s.projectName, s.featureTitle].filter(Boolean).join(' › ')}
                      </div>
                      {s.matchReason && (
                        <div className="text-xs text-zinc-500 mt-0.5 italic">{s.matchReason}</div>
                      )}
                      {s.promptPreview && (
                        <div className="text-xs text-zinc-600 mt-1 line-clamp-2">
                          "{s.promptPreview}"
                        </div>
                      )}
                      {s.taskKeywords.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {s.taskKeywords.slice(0, 8).map((k) => (
                            <span
                              key={k}
                              className="rounded bg-zinc-800/60 border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-400"
                            >
                              {k}
                            </span>
                          ))}
                          {s.taskKeywords.length > 8 && (
                            <span className="text-[10px] text-zinc-500">
                              +{s.taskKeywords.length - 8}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => setReassigning(s)}>
                        <ArrowRightLeft className="size-3.5" /> Riattribuisci
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-5">
              <Empty
                icon={Sparkles}
                title="Nessuna sessione recente"
                description="Le sessioni live appariranno qui appena Claude Code locale invierà gli hook. Configura /setup se non l'hai ancora fatto."
              />
            </div>
          )}
        </CardBody>
      </Card>

      {reassigning && (
        <ReassignDialog
          session={reassigning}
          onClose={() => setReassigning(null)}
          onDone={() => {
            setReassigning(null)
            qc.invalidateQueries({ queryKey: ['time', 'live'] })
          }}
        />
      )}
    </>
  )
}

// ============================================================
// Riattribuzione one-click
// ============================================================

function ReassignDialog({
  session,
  onClose,
  onDone,
}: {
  session: LiveSession
  onClose: () => void
  onDone: () => void
}) {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Task | null>(null)
  const [learnKeywords, setLearnKeywords] = useState(true)

  const treeQuery = useQuery({
    queryKey: ['agile', 'tree-all'],
    queryFn: () =>
      apiClient.get<{
        features: { id: string; userStories: { tasks: Task[] }[] }[]
      }>('/agile/tree?activeOnly=false'),
  })

  const flatTasks: Task[] = []
  if (treeQuery.data) {
    for (const f of treeQuery.data.features) {
      for (const us of f.userStories ?? []) {
        for (const t of us.tasks ?? []) flatTasks.push(t)
      }
    }
  }
  const q = search.trim().toLowerCase()
  const filtered = q
    ? flatTasks.filter((t) => t.title.toLowerCase().includes(q))
    : flatTasks.slice(0, 20)

  const reattribute = useMutation({
    mutationFn: () =>
      apiClient.post('/time/reattribute', {
        sessionId: session.id,
        newTaskId: selected?.id,
        learnKeywords,
        reason: 'Riattribuita da /timetrack/live',
      }),
    onSuccess: onDone,
  })

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 bg-zinc-950/70 backdrop-blur-sm"
        aria-label="Chiudi"
      />
      <div className="relative w-full max-w-2xl rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl overflow-hidden">
        <header className="px-5 py-4 border-b border-zinc-800">
          <h2 className="text-base font-semibold text-zinc-50">Riattribuisci sessione</h2>
          <p className="text-xs text-zinc-400 mt-0.5">
            Sessione attualmente su: <span className="text-zinc-200">{session.taskTitle}</span>
          </p>
          {session.promptPreview && (
            <p className="text-xs text-zinc-500 mt-1 italic line-clamp-2">
              "{session.promptPreview}"
            </p>
          )}
        </header>
        <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
          <input
            placeholder="Cerca task per titolo…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            className="w-full rounded-md bg-zinc-950 border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
          />
          <ul className="space-y-1">
            {filtered.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setSelected(t)}
                  className={cn(
                    'w-full text-left px-3 py-2 rounded-md text-sm transition',
                    selected?.id === t.id
                      ? 'bg-cyan-500/15 text-cyan-200 border border-cyan-500/40'
                      : 'hover:bg-zinc-800/50 text-zinc-300',
                  )}
                >
                  {t.title}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="text-xs text-zinc-500 px-3 py-2">Nessun task trovato.</li>
            )}
          </ul>
          <label className="flex items-center gap-2 text-sm text-zinc-300 pt-2 border-t border-zinc-800/60">
            <input
              type="checkbox"
              checked={learnKeywords}
              onChange={(e) => setLearnKeywords(e.currentTarget.checked)}
            />
            Impara keyword dal prompt e aggiungile al task selezionato
          </label>
        </div>
        <footer className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            type="button"
            onClick={() => reattribute.mutate()}
            disabled={!selected || reattribute.isPending}
          >
            <ArrowRightLeft className="size-4" />{' '}
            {reattribute.isPending ? 'Riattribuzione…' : 'Conferma'}
          </Button>
        </footer>
      </div>
    </div>
  )
}
